// ICIC_POSTER_APP_INTEGRATION_V1
import { getEffectiveToday } from '../../utils/systemTime';
import { useState, useEffect } from 'react';
import { useStudentDataRefreshTick } from '../../utils/studentDataRefresh';
import {
  getStudentHonorsPortalState,
  isCurrentHonorsProgramMember
} from '../../utils/honorsSignups';
import StudentGlobalGroups, {
  studentHasAnyCurrentGroups
} from './StudentGlobalGroups';
import StudentGlobalSchedule from './StudentGlobalSchedule';
import Chat from './chat/Chat';
import HonorsPollForm from './HonorsPollForm';
import {
  listStudentHonorsPolls
} from '../../utils/honorsPolls';

import {
  loadIcicConferenceState,
  loadIcicPosterState
} from '../../utils/icic';

import {
  fetchVisibleStudentNotifications
} from '../../utils/studentNotifications';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


async function fetchCurrentEnrolments(studentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&active=eq.true&select=*,courses(*,course_titles(name),semesters(name,start_date))`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  if (!Array.isArray(data)) return [];

  const today = getEffectiveToday();
  return data.filter(e => {
    if (!e.courses?.is_live) return false;
    const startDate = e.courses?.semesters?.start_date;
    if (!startDate) return false;
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + 18 * 7);
    return new Date(today) <= end;
  });
}


async function fetchPreviousEnrolments(studentId) {
  const today = getEffectiveToday();
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${studentId}&select=*,courses(id,name,course_title_id,semester_id,course_series_id,course_image_drive_file_id,course_titles(name),semesters(name,start_date))`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();

  return data.filter(e => {
    const startDate = e.courses?.semesters?.start_date;
    if (!startDate) return false;
    const start = new Date(startDate);
    const end = new Date(start);
    end.setDate(end.getDate() + 18 * 7);
    const now = new Date(today);
    return now > end;
  });
}


function getCourseName(enrolment) {
  return enrolment.courses?.course_titles?.name ||
    enrolment.courses?.name ||
    'Untitled Course';
}


function getCourseSeriesKey(enrolment) {
  return (
    enrolment?.courses?.course_series_id ||
    (
      enrolment?.courses?.id
        ? `course:${enrolment.courses.id}`
        : `enrolment:${enrolment?.id || 'unknown'}`
    )
  );
}


function groupEnrolmentsBySeries(enrolments) {
  const bySeries = new Map();

  (Array.isArray(enrolments) ? enrolments : [])
    .forEach(enrolment => {
      const key = getCourseSeriesKey(enrolment);

      if (!bySeries.has(key)) {
        bySeries.set(key, []);
      }

      bySeries.get(key).push(enrolment);
    });

  return [...bySeries.entries()]
    .map(([key, rows]) => {
      const ordered = [...rows].sort((a, b) =>
        String(
          b?.courses?.semesters?.start_date || ''
        ).localeCompare(
          String(
            a?.courses?.semesters?.start_date || ''
          )
        )
      );

      const today =
        getEffectiveToday();

      const started =
        ordered.filter(
          enrolment =>
            !enrolment
              ?.courses
              ?.semesters
              ?.start_date ||
            enrolment
              .courses
              .semesters
              .start_date <= today
        );

      const representative =
        started[0] ||
        ordered[
          ordered.length - 1
        ] ||
        null;

      return {
        key,
        enrolments: ordered,
        representative
      };
    })
    .filter(group => group.representative)
    .sort((a, b) =>
      getCourseName(a.representative).localeCompare(
        getCourseName(b.representative)
      )
    );
}


function getCourseSchedule(course) {
  const days =
    Array.isArray(course?.class_days) && course.class_days.length
      ? course.class_days.filter(Boolean)
      : course?.class_day
        ? [course.class_day]
        : [];

  if (!days.length) return '';

  return days.map(day => {
    const time = course?.class_times?.[day];
    const room = course?.classrooms?.[day];
    const timePart = time
      ? time.slice(0, 5)
      : days.length === 1 && course?.class_time
        ? course.class_time.slice(0, 5)
        : null;
    return [day, timePart, room].filter(Boolean).join(' ');
  }).join(' · ');
}


function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}


function Icon({ name, size = 18 }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true
  };

  const paths = {
    home: (
      <>
        <path d="M3 10.8 12 3l9 7.8" />
        <path d="M5.5 9.5V21h13V9.5" />
        <path d="M9.5 21v-6h5v6" />
      </>
    ),
    courses: (
      <>
        <rect x="4" y="4" width="16" height="16" rx="1.5" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 2.5 2.5L16.5 9" />
      </>
    ),
    schedule: (<><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></>),
    groups: (
      <>
        <circle cx="8" cy="9" r="3" />
        <circle cx="16" cy="9" r="3" />
        <path d="M3 20a5 5 0 0 1 10 0" />
        <path d="M11 20a5 5 0 0 1 10 0" />
      </>
    ),
    chat: (
      <>
        <path d="M4 5.5h16v11H9l-5 4v-15Z" />
        <path d="M8 10h8M8 13h5" />
      </>
    ),
    book: (
      <>
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5v-16Z" />
        <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5v-16Z" />
      </>
    ),
    archive: (
      <>
        <path d="M4 7h16v13H4z" />
        <path d="M3 4h18v3H3zM9 11h6" />
      </>
    ),
    profile: (
      <>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5.5 20c.6-4 3-6 6.5-6s5.9 2 6.5 6" />
      </>
    ),
    devices: (
      <>
        <rect x="6.5" y="2.5" width="11" height="19" rx="1.8" />
        <path d="M10 18.5h4" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path d="M10 21h4" />
      </>
    ),
    arrow: <path d="M5 12h14m-5-5 5 5-5 5" />,
    logout: (
      <>
        <path d="M10 5H5v14h5" />
        <path d="M13 8l4 4-4 4M17 12H9" />
      </>
    )
  };

  return <svg {...common}>{paths[name] || paths.courses}</svg>;
}


function CourseArtwork({ course, courseName }) {
  const fileId = course?.course_image_drive_file_id;

  if (fileId) {
    return (
      <img
        className="sp-course-image"
        src={`https://lh3.googleusercontent.com/d/${fileId}`}
        alt=""
      />
    );
  }

  const initials = courseName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();

  return (
    <div className="sp-course-fallback" aria-hidden="true">
      <span>{initials || 'C'}</span>
    </div>
  );
}


export default function StudentMain({
  session,
  onSelectCourse,
  onOpenChat,
  onOpenDashboard,
  onOpenCourses,
  onOpenSchedule,
  onOpenIcic,
  onOpenIcicPoster,
  onOpenGroups,
  onOpenAllReadings,
  section = 'dashboard',
  initialChatId = null,
  onOpenDevices,
  onOpenProfile,
  onOpenHonors,
  onLogout
}) {
  const [currentEnrolments, setCurrentEnrolments] = useState([]);
  const [previousEnrolments, setPreviousEnrolments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPrevious, setShowPrevious] = useState(false);
  const [hasGroups, setHasGroups] = useState(false);

  const [
    honorsPortalState,
    setHonorsPortalState
  ] = useState(null);

  const [
    honorsPolls,
    setHonorsPolls
  ] = useState([]);

  const [
    selectedPollId,
    setSelectedPollId
  ] = useState(null);

  const [
    icicAvailable,
    setIcicAvailable
  ] = useState(false);

  const [
    icicPosterState,
    setIcicPosterState
  ] = useState(null);

  const refreshTick = useStudentDataRefreshTick();


  useEffect(() => {
    let cancelled =
      false;

    loadIcicConferenceState(
      session,
      'student'
    )
      .then(
        state => {
          if (!cancelled) {
            setIcicAvailable(
              state?.available ===
                true
            );
          }
        }
      )
      .catch(
        error => {
          console.error(
            'Loading ICIC availability failed:',
            error
          );

          if (!cancelled) {
            setIcicAvailable(
              false
            );
          }
        }
      );

    return () => {
      cancelled =
        true;
    };
  }, [
    session,
    refreshTick
  ]);


  useEffect(() => {
    let cancelled = false;

    loadIcicPosterState(session)
      .then(state => {
        if (!cancelled) setIcicPosterState(state);
      })
      .catch(error => {
        console.error(
          'Loading ICIC poster dashboard state failed:',
          error
        );
        if (!cancelled) setIcicPosterState(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    session,
    refreshTick
  ]);


  useEffect(() => {
    let cancelled =
      false;

    listStudentHonorsPolls(
      session
    )
      .then(result => {
        if (!cancelled) {
          setHonorsPolls(
            Array.isArray(
              result?.polls
            )
              ? result.polls
              : []
          );
        }
      })
      .catch(error => {
        console.error(
          'Loading Honors Polls failed:',
          error
        );

        if (!cancelled) {
          setHonorsPolls([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    session,
    refreshTick
  ]);



  useEffect(() => {
  let cancelled = false;

  async function loadDashboard() {
    try {
      const [curr, prev] = await Promise.all([
        fetchCurrentEnrolments(session.studentId),
        fetchPreviousEnrolments(session.studentId)
      ]);

      if (cancelled) return;

      setCurrentEnrolments(curr);
      setPreviousEnrolments(prev);

      try {
        const groupsAvailable =
          await studentHasAnyCurrentGroups(
            curr
          );

        if (!cancelled) {
          setHasGroups(
            groupsAvailable
          );
        }

      } catch (error) {
        console.error(
          'Failed to check student Groups:',
          error
        );

        if (!cancelled) {
          setHasGroups(
            false
          );
        }
      }


      try {
        const honorsState =
          await getStudentHonorsPortalState(
            session
          );

        if (!cancelled) {
          setHonorsPortalState(
            honorsState
          );
        }

      } catch (error) {
        console.error(
          'Failed to load Tunghai Honors Program state:',
          error
        );

        if (!cancelled) {
          setHonorsPortalState(
            null
          );
        }
      }


      const courseIds = curr
        .map(enrolment => enrolment.courses?.id)
        .filter(Boolean);

      try {
        const notifs =
          await fetchVisibleStudentNotifications(
            session,
            courseIds,
            3
          );

        if (!cancelled) {
          setNotifications(
            Array.isArray(notifs)
              ? notifs
              : []
          );
        }
      } catch (error) {
        console.error(
          'Failed to load notifications:',
          error
        );

        if (!cancelled) {
          setNotifications([]);
        }
      }
    } catch (error) {
      console.error(
        'Failed to load student dashboard:',
        error
      );
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  }

  loadDashboard();

  return () => {
    cancelled = true;
  };
}, [session, refreshTick]);


  const studentName =
    session.englishName ||
    session.chineseName ||
    session.studentCode;

  const currentCourseSeries =
    groupEnrolmentsBySeries(
      currentEnrolments
    );

  const currentSeriesKeys =
    new Set(
      currentCourseSeries.map(
        group => group.key
      )
    );

  const previousCourseSeries =
    groupEnrolmentsBySeries(
      previousEnrolments
    ).filter(
      group =>
        !currentSeriesKeys.has(
          group.key
        )
    );


  const honorsProgram =
    honorsPortalState?.program ||
    null;


  const isHonorsMember =
    isCurrentHonorsProgramMember(
      honorsPortalState
    );


  const honorsRegistration =
    honorsPortalState
      ?.courseRegistration ||
    null;


  const honorsSelections =
    Array.isArray(
      honorsRegistration
        ?.selections
    )
      ? honorsRegistration.selections
      : [];


  const honorsRecommendations =
    Array.isArray(
      honorsRegistration
        ?.recommendations
    )
      ? honorsRegistration.recommendations
      : [];


  const honorsStatusCounts =
    honorsSelections.reduce(
      (counts, row) => {
        const label =
          row.displayStatus ||
          'Pending';

        counts[label] =
          (
            counts[label] ||
            0
          ) + 1;

        return counts;
      },
      {}
    );


  const honorsStatusSummary =
    [
      'Accepted',
      'Waitlisted',
      'Pending',
      'Not accepted'
    ]
      .filter(
        label =>
          honorsStatusCounts[
            label
          ]
      )
      .map(
        label =>
          `${
            honorsStatusCounts[
              label
            ]
          } ${
            label.toLowerCase()
          }`
      )
      .join(' · ');


  const showHonorsDashboard =
    Boolean(
      honorsRegistration
        ?.show
    );

  const selectedHonorsPoll =
    honorsPolls.find(
      poll =>
        String(
          poll.pollId
        ) ===
        String(
          selectedPollId
        )
    ) || null;


  const hasClassChat =
    currentEnrolments.some(
      enrolment =>
        enrolment
          ?.courses
          ?.chat_enabled !==
        false
    );


  if (loading) {
    return (
      <div className="sp-loading-shell">
        <style>{studentPortalCss}</style>
        <div className="sp-loading">Loading...</div>
      </div>
    );
  }


  return (
    <div className="sp-shell">
      <style>{studentPortalCss}</style>

      <aside className="sp-sidebar">
        <div className="sp-brand">
          <div className="sp-brand-mark">S</div>
          <div>
            <div className="sp-brand-title">Student Portal</div>
            <div className="sp-brand-subtitle">Academic Hub</div>
          </div>
        </div>

        <nav className="sp-nav" aria-label="Student navigation">
          <button
            className={
              section ===
              'dashboard'
                ? 'sp-nav-item active'
                : 'sp-nav-item'
            }
            type="button"
            onClick={
              onOpenDashboard
            }
          >
            <Icon name="home" />
            <span>
              Dashboard
            </span>
          </button>

          <button
            className={
              section === 'courses'
                ? 'sp-nav-item active'
                : 'sp-nav-item'
            }
            type="button"
            onClick={onOpenCourses}
          >
            <Icon name="courses" />
            <span>My Courses</span>
          </button>

          <button className={section === 'schedule' ? 'sp-nav-item active' : 'sp-nav-item'} type="button" onClick={onOpenSchedule}>
            <Icon name="schedule" /><span>Schedule</span>
          </button>

          {hasGroups && (
            <button
              className={
                section ===
                'groups'
                  ? 'sp-nav-item active'
                  : 'sp-nav-item'
              }
              type="button"
              onClick={
                onOpenGroups
              }
            >
              <Icon
                name="groups"
              />

              <span>
                Groups
              </span>
            </button>
          )}

          {hasClassChat && (
            <button
              className={
                section === 'chat'
                  ? 'sp-nav-item active'
                  : 'sp-nav-item'
              }
              type="button"
              onClick={() =>
                onOpenChat?.()
              }
            >
              <Icon name="chat" />
              <span>Class Chat</span>
            </button>
          )}

          {isHonorsMember && (
            <button className="sp-nav-item" type="button" onClick={onOpenAllReadings}>
              <Icon name="book" />
              <span>Course Readings</span>
            </button>
          )}

          <button
            className="sp-nav-item"
            type="button"
            onClick={onOpenProfile}
          >
            <Icon name="profile" />
            <span>Profile</span>
          </button>

          <button
            className="sp-nav-item"
            type="button"
            onClick={onOpenDevices}
          >
            <Icon name="devices" />
            <span>Devices</span>
          </button>
        </nav>

        <div className="sp-sidebar-footer">
          <div className="sp-profile">
            <div className="sp-profile-initial">
              {String(studentName || 'S').trim().charAt(0).toUpperCase()}
            </div>
            <div className="sp-profile-copy">
              <strong>{studentName}</strong>
              <span>{session.studentCode}</span>
            </div>
          </div>

          <button className="sp-signout" type="button" onClick={onLogout}>
            <Icon name="logout" size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      <main className="sp-main">
        <header className="sp-topbar">
          <div>
            <div className="sp-eyebrow">Student Portal</div>
            <h1>
              {section === 'schedule'
                ? 'Schedule'
                : section === 'groups'
                  ? 'Groups'
                  : section === 'chat'
                    ? 'Chat'
                  : section === 'courses'
                    ? 'My Courses'
                    : <>{'Welcome back, '}{studentName}</>}
            </h1>
          </div>

          <button className="sp-mobile-signout" type="button" onClick={onLogout}>
            Sign Out
          </button>
        </header>

        <div className="sp-content">
          {section === 'schedule' ? (
            <StudentGlobalSchedule
              session={session}
              enrolments={currentEnrolments}
              honorsPortalState={
                honorsPortalState
              }
              compact
            />
          ) : section === 'groups' ? (
            <StudentGlobalGroups
              session={session}
              enrolments={currentEnrolments}
              compact
            />
          ) : section === 'chat' ? (
            <Chat
              session={session}
              initialChatId={
                initialChatId
              }
            />
          ) : section === 'courses' ? (
            <>
              <section className="sp-section">
                <div className="sp-section-heading">
                  <div>
                    <div className="sp-section-kicker">
                      Current semester
                    </div>

                    <h2>
                      Current Courses
                    </h2>
                  </div>

                  <div className="sp-count">
                    {currentCourseSeries.length} current
                  </div>
                </div>

                {currentCourseSeries.length === 0 ? (
                  <div className="sp-empty">
                    No current courses found.
                  </div>
                ) : (
                  <div className="sp-course-grid">
                    {currentCourseSeries.map(group => {
                      const enrolment =
                        group.representative;

                      const courseName =
                        getCourseName(
                          enrolment
                        );

                      const course =
                        enrolment.courses;


                      return (
                        <button
                          key={group.key}
                          className="sp-course-card"
                          type="button"
                          onClick={() =>
                            onSelectCourse(
                              course?.id
                            )
                          }
                        >
                          <div className="sp-course-art">
                            <CourseArtwork
                              course={course}
                              courseName={
                                courseName
                              }
                            />
                          </div>

                          <div className="sp-course-card-body">
                            <div className="sp-course-title">
                              {courseName}
                            </div>

                            <div className="sp-course-meta">
                              {getCourseSchedule(
                                course
                              ) ||
                                'Schedule not set'}
                            </div>

                            <div className="sp-course-footer">
                              <span>
                                {course
                                  ?.semesters
                                  ?.name ||
                                  'Current course'}
                              </span>

                              <span className="sp-course-arrow">
                                <Icon
                                  name="arrow"
                                  size={17}
                                />
                              </span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>


              <section className="sp-section sp-previous-section">
                <div className="sp-section-heading">
                  <div>
                    <div className="sp-section-kicker">
                      Archive
                    </div>

                    <h2>
                      Previous Courses
                    </h2>
                  </div>

                  <div className="sp-count">
                    {previousCourseSeries.length} previous
                  </div>
                </div>

                {previousCourseSeries.length === 0 ? (
                  <div className="sp-empty">
                    No previous courses found.
                  </div>
                ) : (
                  <div className="sp-previous-list">
                    {previousCourseSeries.map(group => {
                      const enrolment =
                        group.representative;


                      return (
                        <button
                          key={group.key}
                          type="button"
                          onClick={() =>
                            onSelectCourse(
                              enrolment
                                .courses?.id
                            )
                          }
                        >
                          <span>
                            <strong>
                              {getCourseName(
                                enrolment
                              )}
                            </strong>

                            <small>
                              {enrolment
                                .courses
                                ?.semesters
                                ?.name}
                            </small>
                          </span>

                          <Icon
                            name="arrow"
                            size={17}
                          />
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          ) : selectedHonorsPoll ? (
            <HonorsPollForm
              session={session}
              poll={selectedHonorsPoll}
              onCancel={() =>
                setSelectedPollId(
                  null
                )
              }
              onSubmitted={pollId => {
                setHonorsPolls(
                  current =>
                    current.filter(
                      poll =>
                        String(
                          poll.pollId
                        ) !==
                        String(
                          pollId
                        )
                    )
                );

                setSelectedPollId(
                  null
                );
              }}
            />
          ) : (
            <>
          {honorsPolls.length > 0 && (
            <section
              className="sp-panel"
              style={{
                marginBottom: 24
              }}
            >
              <div className="sp-panel-heading">
                <div>
                  <div className="sp-section-kicker">
                    Tunghai Honors Program
                  </div>

                  <h2>
                    Polls
                  </h2>
                </div>
              </div>

              <div className="sp-quick-list">
                {honorsPolls.map(
                  poll => (
                    <button
                      type="button"
                      key={poll.pollId}
                      onClick={() =>
                        setSelectedPollId(
                          poll.pollId
                        )
                      }
                    >
                      <span className="sp-quick-icon">
                        <Icon name="check" />
                      </span>

                      <span>
                        <strong>
                          {poll.title}
                        </strong>

                        <small>
                          Closes {
                            formatDate(
                              poll.closeDate
                            )
                          }
                        </small>
                      </span>

                      <Icon
                        name="arrow"
                        size={16}
                      />
                    </button>
                  )
                )}
              </div>
            </section>
          )}

          {showHonorsDashboard && (
            <section
              className="sp-panel"
              style={{
                marginBottom: 24
              }}
            >
              <div className="sp-panel-heading">
                <div>
                  <div className="sp-section-kicker">
                    Tunghai Honors Program
                  </div>

                  <h2>
                    Honors
                  </h2>
                </div>
              </div>


              <div className="sp-quick-list">
                {honorsRegistration
                  ?.show && (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenHonors?.(
                        'course_registration'
                      )
                    }
                  >
                    <span className="sp-quick-icon">
                      <Icon name="courses" />
                    </span>

                    <span>
                      <strong>
                        Course Registration
                      </strong>

                      <small>
                        {honorsStatusSummary
                          ||
                          (
                            honorsRegistration.open
                              ? 'Course signup is open.'
                              : 'View your current registration status.'
                          )}

                        {honorsRecommendations.length
                          ? ` · Recommended for ${
                              honorsRecommendations.length
                            } course${
                              honorsRecommendations.length === 1
                                ? ''
                                : 's'
                            }`
                          : ''}
                      </small>
                    </span>

                    <Icon
                      name="arrow"
                      size={16}
                    />
                  </button>
                )}
              </div>
            </section>
          )}


          <section id="student-current-courses" className="sp-section">
            <div className="sp-section-heading">
              <div>
                <div className="sp-section-kicker">Current semester</div>
                <h2>My Courses</h2>
              </div>
              <div className="sp-count">
                {currentCourseSeries.length} current
              </div>
            </div>

            {currentCourseSeries.length === 0 ? (
              <div className="sp-empty">
                No current courses found.
              </div>
            ) : (
              <div className="sp-course-grid">
                {currentCourseSeries.map(group => {
                  const enrolment =
                    group.representative;
                  const courseName =
                    getCourseName(enrolment);
                  const course =
                    enrolment.courses;

                  return (
                    <button
                      key={group.key}
                      className="sp-course-card"
                      type="button"
                      onClick={() =>
                        onSelectCourse(
                          course?.id
                        )
                      }
                    >
                      <div className="sp-course-art">
                        <CourseArtwork
                          course={course}
                          courseName={courseName}
                        />
                      </div>

                      <div className="sp-course-card-body">
                        <div className="sp-course-title">
                          {courseName}
                        </div>

                        <div className="sp-course-meta">
                          {getCourseSchedule(course) ||
                            'Schedule not set'}
                        </div>

                        <div className="sp-course-footer">
                          <span>
                            {course?.semesters?.name ||
                              'Current course'}
                          </span>

                          <span className="sp-course-arrow">
                            <Icon
                              name="arrow"
                              size={17}
                            />
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <div className="sp-dashboard-grid">
            <section className="sp-panel">
              <div className="sp-panel-heading">
                <div className="sp-panel-heading-left">
                  <Icon name="bell" size={18} />
                  <h2>Notifications</h2>
                </div>
              </div>

              {notifications.length === 0 ? (
                <div className="sp-empty compact">No notifications.</div>
              ) : (
                <div className="sp-notification-list">
                  {notifications.map(n => (
                    <div key={n.id} className="sp-notification">
                      <div className="sp-notification-top">
                        <span className="sp-notification-source">
                          {n.courses?.course_titles?.name || 'System'}
                        </span>
                        <span className="sp-notification-date">
                          {formatDate(n.created_at)}
                        </span>
                      </div>
                      <div className="sp-notification-message">
                        {n.message}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="sp-panel">
              <div className="sp-panel-heading">
                <h2>Quick Access</h2>
              </div>

              <div className="sp-quick-list">
                {hasClassChat && (
                  <button
                    type="button"
                    onClick={() => onOpenChat?.()}
                   
                  >
                    <span className="sp-quick-icon">
                      <Icon name="chat" />
                    </span>

                    <span>
                      <strong>
                        Class Chat
                      </strong>

                      <small>
                        {false
                          ? 'Opening...'
                          : 'Open your recent chat'}
                      </small>
                    </span>

                    <Icon
                      name="arrow"
                      size={16}
                    />
                  </button>
                )}

                {isHonorsMember && (
                  <button type="button" onClick={onOpenAllReadings}>
                    <span className="sp-quick-icon"><Icon name="book" /></span>
                    <span>
                      <strong>Course Readings</strong>
                      <small>Browse all courses</small>
                    </span>
                    <Icon name="arrow" size={16} />
                  </button>
                )}

                {icicAvailable && (
                <button
                  type="button"
                  onClick={onOpenIcic}
                >
                  <span className="sp-quick-icon">
                    <Icon name="schedule" />
                  </span>

                  <span>
                    <strong>
                      ICIC Conference
                    </strong>

                    <small>
                      Programme, talks, posters and saved sessions
                    </small>
                  </span>

                  <Icon
                    name="arrow"
                    size={16}
                  />
                </button>
                )}

                {icicPosterState?.available &&
                  (
                    icicPosterState.submitOpen ||
                    (
                      Array.isArray(icicPosterState.posters) &&
                      icicPosterState.posters.length > 0
                    )
                  ) && (
                  <button
                    type="button"
                    onClick={onOpenIcicPoster}
                  >
                    <span className="sp-quick-icon">
                      <Icon name="book" />
                    </span>

                    <span>
                      <strong>
                        {Array.isArray(icicPosterState.posters) &&
                        icicPosterState.posters.length > 0
                          ? 'ICIC Poster Submission'
                          : 'Submit a Poster to ICIC'}
                      </strong>

                      <small>
                        {Array.isArray(icicPosterState.posters) &&
                        icicPosterState.posters.length > 0
                          ? (
                              icicPosterState.posters.length +
                              ' project' +
                              (
                                icicPosterState.posters.length === 1
                                  ? ''
                                  : 's'
                              ) +
                              ' · Status, consent and final poster'
                            )
                          : (
                              icicPosterState?.submission?.closesOn
                                ? (
                                    'Proposals close ' +
                                    icicPosterState.submission.closesOn
                                  )
                                : 'Poster proposals are open'
                            )}
                      </small>
                    </span>

                    <Icon name="arrow" size={16} />
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => setShowPrevious(prev => !prev)}
                >
                  <span className="sp-quick-icon"><Icon name="archive" /></span>
                  <span>
                    <strong>Previous Courses</strong>
                    <small>
                      {previousCourseSeries.length} course
                      {previousCourseSeries.length !== 1 ? 's' : ''}
                    </small>
                  </span>
                  <Icon name="arrow" size={16} />
                </button>
              </div>
            </section>
          </div>

          {honorsProgram
            ?.showApplication && (
            <section
              className="sp-panel"
              style={{
                marginTop: 30
              }}
            >
              <div className="sp-panel-heading">
                <div>
                  <div className="sp-section-kicker">
                    Tunghai Honors Program
                  </div>

                  <h2>
                    Interested in Honors?
                  </h2>
                </div>
              </div>

              <div className="sp-quick-list">
                <button
                  type="button"
                  onClick={() =>
                    onOpenHonors?.(
                      'application'
                    )
                  }
                >
                  <span className="sp-quick-icon">
                    <Icon name="book" />
                  </span>

                  <span>
                    <strong>
                      Apply to the Tunghai Honors Program
                    </strong>

                    <small>
                      {honorsProgram
                        .nextInterviewConsiderationDeadline
                        ? `Applications received by ${
                            formatDate(
                              honorsProgram
                                .nextInterviewConsiderationDeadline
                            )
                          } will be considered for the next interview round.`
                        : 'Applications are open.'}
                    </small>
                  </span>

                  <Icon
                    name="arrow"
                    size={16}
                  />
                </button>
              </div>
            </section>
          )}


          {showPrevious && (
            <section className="sp-section sp-previous-section">
              <div className="sp-section-heading">
                <div>
                  <div className="sp-section-kicker">Archive</div>
                  <h2>Previous Courses</h2>
                </div>
              </div>

              {previousCourseSeries.length === 0 ? (
                <div className="sp-empty">
                  No previous courses found.
                </div>
              ) : (
                <div className="sp-previous-list">
                  {previousCourseSeries.map(group => {
                    const enrolment =
                      group.representative;

                    return (
                      <button
                        key={group.key}
                        type="button"
                        onClick={() =>
                          onSelectCourse(
                            enrolment
                              .courses?.id
                          )
                        }
                      >
                        <span>
                          <strong>
                            {getCourseName(
                              enrolment
                            )}
                          </strong>

                          <small>
                            {enrolment
                              .courses
                              ?.semesters
                              ?.name}
                          </small>
                        </span>

                        <Icon
                          name="arrow"
                          size={17}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}


const studentPortalCss = `
  * {
    box-sizing: border-box;
  }

  .sp-shell,
  .sp-loading-shell {
    min-height: 100vh;
    background: #f7f8fa;
    color: #192431;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .sp-shell {
    display: flex;
  }

  .sp-sidebar {
    width: 248px;
    min-height: 100vh;
    background: #102a43;
    color: #fff;
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    height: 100vh;
    flex-shrink: 0;
  }

  .sp-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 28px 24px 24px;
    border-bottom: 1px solid rgba(255,255,255,0.09);
  }

  .sp-brand-mark {
    width: 38px;
    height: 38px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(255,255,255,0.5);
    font-family: Georgia, "Times New Roman", serif;
    font-size: 22px;
    line-height: 1;
  }

  .sp-brand-title {
    font-family: Georgia, "Times New Roman", serif;
    font-size: 17px;
    letter-spacing: 0.01em;
  }

  .sp-brand-subtitle {
    margin-top: 3px;
    color: #9fb3c8;
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .sp-nav {
    padding: 22px 14px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .sp-nav-item {
    border: 0;
    width: 100%;
    background: transparent;
    color: #c5d3df;
    padding: 11px 12px;
    display: flex;
    align-items: center;
    gap: 11px;
    text-align: left;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border-radius: 4px;
  }

  .sp-nav-item:hover {
    background: rgba(255,255,255,0.07);
    color: #fff;
  }

  .sp-nav-item.active {
    background: #fff;
    color: #102a43;
  }

  .sp-nav-item:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .sp-sidebar-footer {
    margin-top: auto;
    padding: 18px 18px 22px;
    border-top: 1px solid rgba(255,255,255,0.09);
  }

  .sp-profile {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
  }

  .sp-profile-initial {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: #d9e2ec;
    color: #102a43;
    font-weight: 800;
    font-size: 13px;
  }

  .sp-profile-copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .sp-profile-copy strong {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }

  .sp-profile-copy span {
    color: #9fb3c8;
    font-size: 11px;
  }

  .sp-signout {
    width: 100%;
    border: 1px solid rgba(255,255,255,0.15);
    background: transparent;
    color: #d9e2ec;
    padding: 9px 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 7px;
    cursor: pointer;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
  }

  .sp-main {
    min-width: 0;
    flex: 1;
  }

  .sp-topbar {
    min-height: 112px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    padding: 30px 44px 24px;
    background: #fff;
    border-bottom: 1px solid #e3e8ee;
  }

  .sp-eyebrow,
  .sp-section-kicker {
    color: #627d98;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 10px;
    font-weight: 800;
  }

  .sp-topbar h1 {
    margin: 6px 0 0;
    font-family: Georgia, "Times New Roman", serif;
    font-weight: 500;
    color: #102a43;
    font-size: clamp(26px, 3vw, 36px);
    line-height: 1.1;
  }

  .sp-mobile-signout {
    display: none;
  }

  .sp-content {
    max-width: 1260px;
    margin: 0 auto;
    padding: 38px 44px 60px;
  }

  .sp-section {
    scroll-margin-top: 20px;
    margin-bottom: 36px;
  }

  .sp-section-heading,
  .sp-panel-heading {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 16px;
  }

  .sp-section-heading h2,
  .sp-panel-heading h2 {
    margin: 5px 0 0;
    font-family: Georgia, "Times New Roman", serif;
    font-weight: 500;
    color: #102a43;
  }

  .sp-section-heading h2 {
    font-size: 24px;
  }

  .sp-panel-heading h2 {
    font-size: 20px;
  }

  .sp-count {
    color: #627d98;
    font-size: 12px;
  }

  .sp-course-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }

  .sp-course-card {
    padding: 0;
    border: 1px solid #d9e2ec;
    background: #fff;
    text-align: left;
    cursor: pointer;
    overflow: hidden;
    border-radius: 5px;
    transition: transform 140ms ease, border-color 140ms ease, box-shadow 140ms ease;
    display: flex;
flex-direction: column;
height: 100%;
  }

  .sp-course-card:hover {
    transform: translateY(-2px);
    border-color: #9fb3c8;
    box-shadow: 0 8px 24px rgba(16,42,67,0.08);
  }

  .sp-course-art {
  width: 100%;
  aspect-ratio: 16 / 8.8;
  background: #d9e2ec;
  overflow: hidden;
  flex: 0 0 auto;
}

  .sp-course-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .sp-course-fallback {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
    background:
      linear-gradient(135deg, rgba(16,42,67,0.98), rgba(36,76,110,0.78)),
      #102a43;
  }

  .sp-course-fallback span {
    font-family: Georgia, "Times New Roman", serif;
    color: rgba(255,255,255,0.88);
    font-size: 42px;
    letter-spacing: 0.05em;
  }

  .sp-course-card-body {
  padding: 17px 18px 16px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

  .sp-course-title {
    color: #102a43;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 18px;
    line-height: 1.25;
    margin-bottom: 8px;
    min-height: 45px;
  }

  .sp-course-meta {
    color: #627d98;
    font-size: 12px;
    min-height: 18px;
  }

  .sp-course-footer {
    margin-top: auto;
    padding-top: 13px;
    border-top: 1px solid #edf1f5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #486581;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .sp-course-arrow {
    display: inline-flex;
    color: #2f6f9f;
  }

  .sp-dashboard-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.35fr) minmax(300px, 0.65fr);
    gap: 22px;
  }

  .sp-panel {
    background: #fff;
    border: 1px solid #d9e2ec;
    padding: 22px 24px;
    border-radius: 5px;
  }

  .sp-panel-heading {
    align-items: center;
    padding-bottom: 14px;
    border-bottom: 1px solid #edf1f5;
  }

  .sp-panel-heading-left {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #486581;
  }

  .sp-notification {
    padding: 14px 0;
    border-bottom: 1px solid #edf1f5;
  }

  .sp-notification:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  .sp-notification-top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 5px;
  }

  .sp-notification-source {
    color: #2f6f9f;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .sp-notification-date {
    color: #829ab1;
    font-size: 11px;
    white-space: nowrap;
  }

  .sp-notification-message {
    color: #334e68;
    font-size: 13px;
    line-height: 1.55;
  }

  .sp-quick-list {
    display: flex;
    flex-direction: column;
  }

  .sp-quick-list button {
    width: 100%;
    display: grid;
    grid-template-columns: 34px 1fr 18px;
    align-items: center;
    gap: 11px;
    padding: 13px 0;
    background: transparent;
    border: 0;
    border-bottom: 1px solid #edf1f5;
    cursor: pointer;
    text-align: left;
    color: #486581;
  }

  .sp-quick-list button:last-child {
    border-bottom: 0;
  }

  .sp-quick-list button:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .sp-quick-icon {
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    background: #eef4f8;
    color: #2f6f9f;
    border-radius: 4px;
  }

  .sp-quick-list button > span:nth-child(2) {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .sp-quick-list strong {
    color: #243b53;
    font-size: 13px;
  }

  .sp-quick-list small {
    color: #829ab1;
    font-size: 11px;
  }

  .sp-previous-section {
    margin-top: 34px;
  }

  .sp-previous-list {
    background: #fff;
    border: 1px solid #d9e2ec;
    border-radius: 5px;
  }

  .sp-previous-list button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    background: transparent;
    border: 0;
    border-bottom: 1px solid #edf1f5;
    padding: 15px 18px;
    cursor: pointer;
    text-align: left;
    color: #486581;
  }

  .sp-previous-list button:last-child {
    border-bottom: 0;
  }

  .sp-previous-list button > span {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .sp-previous-list strong {
    color: #243b53;
    font-family: Georgia, "Times New Roman", serif;
    font-weight: 500;
    font-size: 15px;
  }

  .sp-previous-list small {
    color: #829ab1;
    font-size: 11px;
  }

  .sp-empty {
    border: 1px dashed #bcccdc;
    background: #fff;
    color: #627d98;
    padding: 22px;
    border-radius: 5px;
    font-size: 13px;
  }

  .sp-empty.compact {
    border: 0;
    padding: 12px 0 0;
  }

  .sp-loading-shell {
    display: grid;
    place-items: center;
  }

  .sp-loading {
    color: #627d98;
    font-size: 13px;
  }

  @media (max-width: 1050px) {
    .sp-course-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .sp-dashboard-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 820px) {
    .sp-shell {
      display: block;
    }

    .sp-sidebar {
      width: 100%;
      height: auto;
      min-height: 0;
      position: static;
    }

    .sp-brand {
      padding: 16px 18px;
    }

    .sp-nav {
      padding: 8px 12px 12px;
      flex-direction: row;
      overflow-x: auto;
    }

    .sp-nav-item {
      width: auto;
      min-width: max-content;
      padding: 9px 11px;
    }

    .sp-sidebar-footer {
      display: none;
    }

    .sp-topbar {
      min-height: 94px;
      padding: 22px 20px 18px;
      align-items: center;
    }

    .sp-mobile-signout {
      display: block;
      border: 1px solid #bcccdc;
      background: #fff;
      color: #334e68;
      border-radius: 4px;
      padding: 7px 10px;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
    }

    .sp-content {
      padding: 26px 20px 44px;
    }
  }

  @media (max-width: 600px) {
    .sp-course-grid {
      grid-template-columns: 1fr;
    }

    .sp-topbar h1 {
      font-size: 26px;
    }

    .sp-section-heading {
      align-items: flex-start;
    }
  }
`;